import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { db } from '@/config/firebase'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  Timestamp,
} from 'firebase/firestore'
import { DealApproval, VehicleDecision, StageDecision, type DealApprovalStageType } from '@otw/models/deals'
// Note: Importing directly until package is rebuilt
import { dealApprovalConverter } from '@otw/models/converters/dealApprovalConverter'

interface UseAdminDealApprovalOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
}

export const useAdminDealApproval = (options?: UseAdminDealApprovalOptions) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()

  const collectionRef = collection(db, 'deal_approval').withConverter(dealApprovalConverter)
  const queryKey = ['deal_approval']

  // Query deals - users see their own deals, deal_approvers (any stage) see all deals
  const {
    data: deals,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<DealApproval[]> => {
      try {
        let q
        const isDealApprover = Boolean(userInfo?.deal_approver)

        if (isDealApprover) {
          // Deal approvers see all deals
          // Try with orderBy first, fallback to no orderBy if index doesn't exist
          try {
            q = query(collectionRef, orderBy('created_at', 'desc'))
          } catch (e) {
            // If orderBy fails (no index), just get all docs
            q = query(collectionRef)
          }
        } else {
          // Regular users see only their own deals
          // Try with orderBy first, fallback to no orderBy if index doesn't exist
          try {
            q = query(
              collectionRef,
              where('created_by', '==', userInfo?.id || ''),
              orderBy('created_at', 'desc')
            )
          } catch (e) {
            // If orderBy fails (no index), just use where clause
            q = query(
              collectionRef,
              where('created_by', '==', userInfo?.id || '')
            )
          }
        }

        const snapshot = await getDocs(q)
        const deals = snapshot.docs.map(doc => doc.data())
        
        // Sort in memory if orderBy failed
        return deals.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime()
          const dateB = new Date(b.created_at).getTime()
          return dateB - dateA
        })
      } catch (error) {
        console.error('Error fetching deal approvals:', error)
        // If query fails completely, try a simpler query
        try {
          const snapshot = await getDocs(collectionRef)
          const allDeals = snapshot.docs.map(doc => doc.data())
          
          // Filter by user if not deal approver
          const filtered = userInfo?.deal_approver != null
            ? allDeals
            : allDeals.filter(deal => deal.created_by === userInfo?.id)
          
          // Sort in memory
          return filtered.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime()
            const dateB = new Date(b.created_at).getTime()
            return dateB - dateA
          })
        } catch (fallbackError) {
          console.error('Fallback query also failed:', fallbackError)
          throw fallbackError
        }
      }
    },
    enabled: !!userInfo?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const refetchDeals = async () => {
    await refetch()
  }

  // Create a new deal approval
  const createDealApproval = useMutation({
    mutationFn: async (data: {
      vehicle_ids: string[]
      notes?: string
      /** Stock # and VIN per vehicle (same order as vehicle_ids) for notifications */
      vehicle_summaries?: { stock_number: string; vin: string }[]
    }): Promise<string> => {
      if (!userInfo?.id) {
        throw new Error('User not authenticated')
      }

      const now = new Date().toISOString()
      const dealData: Omit<DealApproval, 'id'> = {
        created_by: userInfo.id,
        created_by_name: userInfo.displayName || userInfo.email || 'Unknown',
        created_at: now,
        updated_at: now,
        vehicle_ids: data.vehicle_ids,
        ...(data.vehicle_summaries?.length ? { vehicle_summaries: data.vehicle_summaries } : {}),
        ...(data.notes ? { notes: data.notes } : {}),
      }

      const docRef = await addDoc(collectionRef, dealData)
      return docRef.id
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Deal approval request created successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error creating deal approval request')
      }
      console.error('Error creating deal approval:', error)
      options?.onError?.(error)
    },
  })

  type StageAction = 'approved' | 'rejected' | 'conditionally_approved'

  /** Strip undefined from stage decision and vehicle decisions for Firestore */
  const cleanStageDecision = (s: StageDecision): Record<string, unknown> => {
    const o: Record<string, unknown> = { user_id: s.user_id, status: s.status }
    if (s.note !== undefined && s.note !== '') o.note = s.note
    if (s.at !== undefined) o.at = s.at
    return o
  }
  const stripUndefinedFromVehicleDecisions = (
    decisions: Record<string, VehicleDecision>
  ): Record<string, Record<string, unknown>> => {
    const out: Record<string, Record<string, unknown>> = {}
    for (const [k, v] of Object.entries(decisions)) {
      const cleaned: Record<string, unknown> = {}
      if (v.availability) cleaned.availability = cleanStageDecision(v.availability)
      if (v.condition) cleaned.condition = cleanStageDecision(v.condition)
      if (v.final) cleaned.final = cleanStageDecision(v.final)
      if (Object.keys(cleaned).length > 0) out[k] = cleaned
    }
    return out
  }

  // Update a single vehicle's decision within a deal (approve / conditionally approve / reject)
  const updateDealVehicleDecision = useMutation({
    mutationFn: async (params: {
      dealId: string
      vehicleId: string
      status: StageAction
      note?: string
    }): Promise<void> => {
      if (!userInfo?.id) {
        throw new Error('User not authenticated')
      }

      if (!userInfo?.deal_approver) {
        throw new Error('Only deal approvers can update vehicle decisions')
      }

      const docRef = doc(collectionRef, params.dealId)
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) {
        throw new Error('Deal not found')
      }

      const deal = snapshot.data() as DealApproval
      const existingDecisions = { ...(deal.vehicle_decisions ?? {}) }
      const existing = existingDecisions[params.vehicleId] ?? {}
      const now = new Date().toISOString()
      const approverRole = userInfo.deal_approver as string
      const stage: DealApprovalStageType = approverRole === 'location' ? 'availability' : (approverRole as DealApprovalStageType)

      const stageDecision: StageDecision = {
        user_id: userInfo.id,
        status: params.status,
        at: now,
        ...(params.note !== undefined && params.note !== '' ? { note: params.note } : {}),
      }

      const newDecision: VehicleDecision = {
        ...existing,
        [stage]: stageDecision,
      }

      existingDecisions[params.vehicleId] = newDecision

      await updateDoc(docRef, {
        vehicle_decisions: stripUndefinedFromVehicleDecisions(existingDecisions),
        updated_at: now,
      })
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Vehicle decision updated')
      }
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error updating vehicle decision')
      }
      console.error('Error updating vehicle decision:', error)
      options?.onError?.(error)
    },
  })

  // Set the same decision for multiple vehicles in a deal (used by package-level Approve/Reject)
  const updateDealVehicleDecisionsBulk = useMutation({
    mutationFn: async (params: {
      dealId: string
      vehicleIds: string[]
      status: StageAction
      note?: string
    }): Promise<void> => {
      if (!userInfo?.id) {
        throw new Error('User not authenticated')
      }

      if (!userInfo?.deal_approver) {
        throw new Error('Only deal approvers can update vehicle decisions')
      }

      if (params.vehicleIds.length === 0) return

      const docRef = doc(collectionRef, params.dealId)
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) {
        throw new Error('Deal not found')
      }

      const deal = snapshot.data() as DealApproval
      const existingDecisions = { ...(deal.vehicle_decisions ?? {}) }
      const now = new Date().toISOString()
      const approverRole = userInfo.deal_approver as string
      const stage: DealApprovalStageType = approverRole === 'location' ? 'availability' : (approverRole as DealApprovalStageType)

      const stageDecision: StageDecision = {
        user_id: userInfo.id,
        status: params.status,
        at: now,
        ...(params.note !== undefined && params.note !== '' ? { note: params.note } : {}),
      }

      for (const vehicleId of params.vehicleIds) {
        const existing = existingDecisions[vehicleId] ?? {}
        existingDecisions[vehicleId] = {
          ...existing,
          [stage]: stageDecision,
        }
      }

      await updateDoc(docRef, {
        vehicle_decisions: stripUndefinedFromVehicleDecisions(existingDecisions),
        updated_at: now,
      })
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Vehicle decisions updated')
      }
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error updating vehicle decisions')
      }
      console.error('Error updating vehicle decisions:', error)
      options?.onError?.(error)
    },
  })

  // Delete deal approval (only creator can delete, and only if pending)
  const deleteDealApproval = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!userInfo?.id) {
        throw new Error('User not authenticated')
      }

      // Get the deal to check ownership and status
      const deal = deals?.find(d => d.id === id)
      if (!deal) {
        throw new Error('Deal not found')
      }

      if (deal.created_by !== userInfo.id) {
        throw new Error('You can only delete your own deals')
      }

      const docRef = doc(collectionRef, id)
      await deleteDoc(docRef)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Deal approval deleted successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error deleting deal approval')
      }
      console.error('Error deleting deal approval:', error)
      options?.onError?.(error)
    },
  })

  return {
    deals: deals || [],
    isLoading,
    isError,
    error,
    refetchDeals,
    createDealApproval: createDealApproval.mutate,
    updateDealVehicleDecision: updateDealVehicleDecision.mutate,
    updateDealVehicleDecisionsBulk: updateDealVehicleDecisionsBulk.mutate,
    deleteDealApproval: deleteDealApproval.mutate,
    isCreating: createDealApproval.isPending,
    isUpdatingVehicleDecision: updateDealVehicleDecision.isPending,
    isUpdatingBulkVehicleDecisions: updateDealVehicleDecisionsBulk.isPending,
    isDeleting: deleteDealApproval.isPending,
  }
}
